import React, { useState, useCallback, useEffect } from 'react';

import { Helmet } from 'react-helmet-async';

import {
  Box,
  Divider,
  TextField,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  styled,
  Card,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';

import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import CommonBreadcumbs from '../common/CommonBreadcumbs';
import CommonSectionHeader from '../common/CommonSectionHeader';
import { checkValueExists } from '../../utils/dataSchema';

import { initialValues, validationSchema } from '../../schema/account/accountSchema';
import { useBusiness, businessTypes } from '../../hooks/api/useBusiness';
import { useAccounts, accountTypes, targetTypes } from '../../hooks/api/useAccounts';
import { useRegions, regionTypes, targetTypes as regionTargetTypes } from '../../hooks/api/useRegions';
import { useSystemPrompt } from '../../hooks/useSystemPrompt';
import { useAppContextFeatures } from '../../context/AppContext';
import { useSystemRoutes, routeTypes } from '../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../hooks/useSystemHead';

import { useAuthAccess } from '../../hooks/useAuthAccess';

import { useTranslations } from '../../hooks/useTranslations';

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export default function UpdateAccount({ routeType }) {
  // const { id } = useParams();
  // const id = 1;

  const { t } = useTranslations({ ns: ['@account', 'common'] });

  const { authData } = useAuthAccess();

  const { promptMessage } = useSystemPrompt();
  const { list: listBusiness, filterOperatorTypes } = useBusiness({ businessType: businessTypes.business });
  const { isLoading, get, update } = useAccounts({ accountType: accountTypes.account });
  const { list: listRegions } = useRegions({});
  const [dataCountries, setDataCountries] = useState([]);
  const [dataStates, setDataStates] = useState([]);
  const [dataAreas, setDataAreas] = useState([]);

  const [dataBusiness, setDataBusiness] = useState([]);

  const { setSystemLoading } = useAppContextFeatures();
  const navigate = useNavigate();
  const [savedValues, setSavedValues] = useState(null);
  const { getRoute } = useSystemRoutes(routeType);
  const { getHtmlHead } = useSystemHead(routeType);

  const [id, setId] = useState('');

  const loadData = useCallback(
    async (id) => {
      setSystemLoading(true);

      try {
        const { data } = await get({ id });
        const { account } = data;

        setSavedValues({
          companyName: account.company_name,
          idNumber: account.id_number,
          website: account.website,
          companyContact: account.company_contact,
          email: account.email,
          address1: account.address_1,
          address2: account.address_2,
          countryId: account.country_id,
          stateId: account.state_id,
          areaId: account.area_id,
          postcode: account.postcode,
          name: account.name,
          businessId: account.business_id,
          // status: account?.status === '1',
        });
      } catch (error) {
        promptMessage(false, `${error?.message}`);
      }

      setSystemLoading(false);
    },
    [id]
  );

  const onSubmit = async (currentValues) => {
    try {
      let data = {
        account: {
          name: currentValues?.name,
          business_id: currentValues?.businessId,
        },
      };

      const response = await update({ id, data, targetType: targetTypes.account });

      promptMessage(true, `${response?.message}`);

      loadData(id);
    } catch (error) {
      promptMessage(false, `Action Failed (${error?.message})`);
    }
  };

  const fetchDataBusiness = async () => {
    let filterValues = [];

    try {
      filterValues.push({
        name: 'status',
        operator: filterOperatorTypes.eq,
        value: '1',
      });

      const props = {
        limit: -1,
        skip: 0,
        filterValue: filterValues,
        sortInfo: {
          id: 'company_name',
          dir: 0,
        },
      };

      const { data } = await listBusiness(props);

      setDataBusiness(data?.businesses);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const fetchDataCountry = async () => {
    try {
      const props = {
        limit: -1,
        offset: 0,
        filterValue: [],
        sortInfo: {
          id: 'country_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.country;

      const { data } = await listRegions(props);

      // console.log('re', data);

      setDataCountries(data?.countries);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const fetchDataStates = async (countryId) => {
    setDataStates([]);

    try {
      const props = {
        limit: -1,
        offset: 0,
        filterValue: [],
        sortInfo: {
          id: 'state_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.state;
      props.countryId = countryId;

      const { data } = await listRegions(props);

      setDataStates(data?.states);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const fetchDataAreas = async (stateId) => {
    setDataAreas([]);

    try {
      const props = {
        limit: -1,
        offset: 0,
        filterValue: [],
        sortInfo: {
          id: 'area_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.area;
      props.stateId = stateId;

      const { data } = await listRegions(props);

      setDataAreas(data?.areas);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const formik = useFormik({
    initialValues: savedValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit,
  });

  const { values, errors, touched, handleChange, setFieldValue } = formik;

  useEffect(() => {
    fetchDataCountry();
    fetchDataBusiness();
  }, []);

  // useEffect(() => {

  //     if(id){
  //         loadData(id);
  //     }

  // }, [id])

  useEffect(() => {
    if (authData?.profile?.account?.id) {
      setId(authData.profile.account.id);

      loadData(authData.profile.account.id);
    }
  }, [authData]);

  useEffect(() => {
    if (formik?.values?.countryId) {
      if (dataStates.length) {
        if (dataStates[0].country_id !== formik.values.countryId) {
          fetchDataStates(formik.values.countryId);
        }
      } else {
        fetchDataStates(formik.values.countryId);
      }
    }

    if (formik?.values?.stateId) {
      if (dataAreas.length) {
        if (dataAreas[0].state_id !== formik.values.stateId) {
          fetchDataAreas(formik.values.stateId);
        }
      } else {
        fetchDataAreas(formik.values.stateId);
      }
    }
  }, [formik.values]);

  const checkPostcodeExists = () => {
    const checkExists = getPostcodesArray().find((i) => i === values.postcode);

    if (checkExists) return values.postcode;

    return '';
  };

  const getPostcodesArray = () => {
    let postcodeStringCheck = dataAreas.filter((i) => i.id === formik.values.areaId);

    if (postcodeStringCheck.length <= 0) return [];

    let postcodeString = postcodeStringCheck.length >= 0 ? postcodeStringCheck[0].postcode_string : '';

    let postcodeArray = postcodeString.includes(',') ? postcodeString.split(',') : [postcodeString];

    if (postcodeArray.length <= 0) return [];

    return postcodeArray;
  };

  const RenderAccountInfo = () => {
    return (
      <>
        <Grid item xs={12} sm={12} md={4} textAlign={'left'}>
          <CommonSectionHeader title={t('mainAccount')} subTitle={t('organizationOfAccountDetails')} />
        </Grid>

        <Grid item xs={12} sm={12} md={8}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                autoFocus
                fullWidth
                name={'name'}
                label={t('name')}
                type="text"
                variant="outlined"
                value={values?.name}
                onChange={handleChange}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <FormControl
                margin="dense"
                fullWidth
                size="small"
                error={touched.businessId && Boolean(errors.businessId)}
              >
                <InputLabel id="businessId">{t('business')}</InputLabel>
                <Select
                  disabled={values?.differentBusiness === true}
                  labelId="businessId"
                  label="Business"
                  name={'businessId'}
                  onChange={(event) => {
                    setFieldValue('businessId', event.target.value);
                  }}
                  // value={values?.businessId}
                  value={checkValueExists({
                    dataSearch: dataBusiness,
                    valueToCompare: values?.businessId,
                  })}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {dataBusiness.map((i, index) => {
                    return (
                      <MenuItem key={index} value={i.id}>
                        {i.company_name}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{touched.businessId && errors.businessId}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  const RenderAccountProfile = () => {
    return (
      <>
        <Grid item xs={12} sm={12} md={4} textAlign={'left'}>
          <CommonSectionHeader title={t('billingProfile')} subTitle={t('fullDetailsOfAccountProfile')} />
        </Grid>

        <Grid item xs={12} sm={12} md={8}>
          <Grid container spacing={1}>
            {/* <Grid item xs={12} sm={12} md={6} textAlign="left">
                            <FormControlLabel 
                                control={
                                    <Switch 
                                        sx={{
                                            ml: 2,
                                        }}
                                        name="status" 
                                        checked={Boolean(values?.status)}
                                        onChange={(event, checked) => {
                                            setFieldValue("status", checked);
                                        }}
                                    />
                                } 
                                label={
                                    Boolean(values?.status)? "Enabled" : "Disabled"
                                }
                            />
                        </Grid> */}

            <Grid item xs={12} sm={12} md={6}>
              <TextField
                disabled
                margin="dense"
                size="small"
                autoFocus
                fullWidth
                name={'companyName'}
                label={t('companyName')}
                type="text"
                variant="outlined"
                value={values?.companyName}
                onChange={handleChange}
                error={touched.companyName && Boolean(errors.companyName)}
                helperText={touched.companyName && errors.companyName}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <TextField
                disabled
                margin="dense"
                size="small"
                autoFocus
                fullWidth
                name={'idNumber'}
                label={t('idNumber')}
                type="text"
                variant="outlined"
                value={values?.idNumber}
                onChange={handleChange}
                error={touched.idNumber && Boolean(errors.idNumber)}
                helperText={touched.idNumber && errors.idNumber}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                disabled
                margin="dense"
                size="small"
                autoFocus
                fullWidth
                name={'website'}
                label={t('website')}
                type="text"
                variant="outlined"
                value={values?.website}
                onChange={handleChange}
                error={touched.website && Boolean(errors.website)}
                helperText={touched.website && errors.website}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                disabled
                margin="dense"
                size="small"
                autoFocus
                fullWidth
                name={'email'}
                label={t('email')}
                type="text"
                variant="outlined"
                value={values?.email}
                onChange={handleChange}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                disabled
                margin="dense"
                size="small"
                autoFocus
                fullWidth
                name={'companyContact'}
                label={t('contact')}
                type="text"
                variant="outlined"
                value={values?.companyContact}
                onChange={handleChange}
                error={touched.companyContact && Boolean(errors.companyContact)}
                helperText={touched.companyContact && errors.companyContact}
              />
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  const RenderAccountAddress = () => {
    return (
      <>
        <Grid item xs={12} sm={12} md={4} textAlign={'left'}>
          <CommonSectionHeader title={t('address')} subTitle={t('fullAddressDetailsOfAccountInformation')} />
        </Grid>

        <Grid item xs={12} sm={12} md={8}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                disabled
                margin="dense"
                size="small"
                fullWidth
                name={'address1'}
                label={t('address1')}
                type="text"
                variant="outlined"
                value={values?.address1}
                onChange={handleChange}
                error={touched.address1 && Boolean(errors.address1)}
                helperText={touched.address1 && errors.address1}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                disabled
                margin="dense"
                size="small"
                fullWidth
                name={'address2'}
                label={t('address2')}
                type="text"
                variant="outlined"
                value={values?.address2 ? values.address2 : ''}
                onChange={handleChange}
                error={touched.address2 && Boolean(errors.address2)}
                helperText={touched.address2 && errors.address2}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl
                disabled
                margin="dense"
                fullWidth
                size="small"
                error={touched.countryId && Boolean(errors.countryId)}
              >
                <InputLabel id="country">{t('country')}</InputLabel>
                <Select
                  labelId="countryId"
                  label="Country"
                  name={'countryId'}
                  onChange={(event) => {
                    setFieldValue(`countryId`, event.target.value);
                    setFieldValue(`stateId`, '');
                    setFieldValue(`areaId`, '');
                    setDataStates([]);
                    setDataAreas([]);
                  }}
                  value={checkValueExists({
                    dataSearch: dataCountries,
                    valueToCompare: values?.countryId,
                  })}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {dataCountries.map((i, index) => {
                    return (
                      <MenuItem key={index} value={i.id}>
                        {i.country_name}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{touched.countryId && errors.countryId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl
                disabled
                margin="dense"
                fullWidth
                size="small"
                error={touched.stateId && Boolean(errors.stateId)}
              >
                <InputLabel id="state">{t('state')}</InputLabel>
                <Select
                  labelId="stateId"
                  label="State"
                  name={'stateId'}
                  onChange={(event) => {
                    setFieldValue(`stateId`, event.target.value);
                    setFieldValue(`areaId`, '');
                    setDataAreas([]);
                  }}
                  value={checkValueExists({
                    dataSearch: dataStates,
                    valueToCompare: values?.stateId,
                  })}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {dataStates.map((i, index) => {
                    return (
                      <MenuItem key={index} value={i.id}>
                        {i.state_name}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{touched.stateId && errors.stateId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <FormControl
                disabled
                margin="dense"
                fullWidth
                size="small"
                error={touched.areaId && Boolean(errors.areaId)}
              >
                <InputLabel id="area">{t('area')}</InputLabel>
                <Select
                  labelId="areaId"
                  label="area"
                  name={'areaId'}
                  onChange={(event) => {
                    setFieldValue(`areaId`, event.target.value);
                    setFieldValue(`postcode`, '');
                  }}
                  value={checkValueExists({
                    dataSearch: dataAreas,
                    valueToCompare: values?.areaId,
                  })}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {dataAreas.map((i, index) => {
                    return (
                      <MenuItem key={index} value={i.id}>
                        {i.area_name}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{touched.areaId && errors.areaId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <FormControl
                disabled
                margin="dense"
                fullWidth
                size="small"
                error={touched.postcode && Boolean(errors.postcode)}
              >
                <InputLabel id="postcode">{t('postcode')}</InputLabel>
                <Select
                  labelId="postcode"
                  label="Postcode"
                  name={'postcode'}
                  onChange={handleChange}
                  value={checkPostcodeExists()}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {getPostcodesArray().length ? (
                    getPostcodesArray().map((i, index) => {
                      return (
                        <MenuItem key={index} value={i}>
                          {i}
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem value={''}>{null}</MenuItem>
                  )}
                </Select>
                <FormHelperText>{touched.postcode && errors.postcode}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  const RenderForm = () => {
    if (!values) return <></>;

    return (
      <form onSubmit={formik.handleSubmit}>
        <StyledCard>
          <Grid container spacing={1}>
            {RenderAccountInfo()}

            {/* <Grid item xs={12}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                        </Grid>

                        {
                            RenderAccountProfile()
                        }
                        
                        <Grid item xs={12}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                        </Grid>

                       {
                            RenderAccountAddress()
                       } */}
          </Grid>
        </StyledCard>

        <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
          <Grid item md={12} textAlign={'right'}>
            <LoadingButton
              type="submit"
              // fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
              }}
              loading={isLoading}
            >
              {t('saveChanges')}
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    );
  };

  const RenderFormInfo = () => {
    if (!values) return <></>;

    return (
      <StyledCard>
        <Grid container spacing={1}>
          {RenderAccountProfile()}

          <Grid item xs={12}>
            <Divider sx={{ mt: 1, mb: 1 }} />
          </Grid>

          {RenderAccountAddress()}
        </Grid>
      </StyledCard>
    );
  };

  return (
    <Box>
      {getHtmlHead({ routeType, title: headTitles.account })}

      <CommonBreadcumbs
        routeType={routeType}
        pageTitle={t('account')}
        breadcrumbs={[
          {
            displayText: t('account'),
            props: {
              color: 'secondary.main',
            },
          },
        ]}
      />

      {RenderForm()}

      {RenderFormInfo()}
    </Box>
  );
}
